import { createAsyncThunk } from "@reduxjs/toolkit";
import { ColumnFiltersState } from "@tanstack/react-table";
import { Axios } from "api/axios";
import axios from "axios";
const API_URL = process.env.REACT_APP_BASE_URL;
const headers = {
  "Content-Type": "application/json",
};

export interface ManPower {
  id?: number;
  packageName: string;
  districtCode: string;
  districtName: string;
  state: string;
  status: boolean;
  createBy?: string;
  updatedBy?: string;
  createDate?: string;
  updateDate?: string;
}
export interface PaginatedResponse<T> {
  count: number;
  total_pages: number;
  next: string | null;
  previous: string | null;
  results: T[];
}
export const fetchManPowerList = createAsyncThunk(
  "manPower/fetchManPowerList",
  async ({
    page,
    pageSize,
    search,
    sort,
    order,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: ColumnFiltersState;
  }) => {
    try {
      const params: Record<string, any> = {
        page,
        page_size: pageSize,
      };

      if (search) params.search = search;
      if (sort) params.sort = sort;
      if (order) params.order = order;

      if (filters && Array.isArray(filters)) {
        filters.forEach((filter) => {
          if (filter.id && filter.value !== undefined && filter.value !== "") {
            params[filter.id] = filter.value;
          }
        });
      }

      const response: any = await Axios.get(`/manPowers/`, { params });
      const responseData = response.data;
      return {
        data: responseData.data || responseData.results || [],
        rowCount: responseData.count || 0,
      };
    } catch (error: any) {
      console.error(
        "fetchManPowerList error:",
        error.response?.data || error.message
      );
      throw new Error("Failed to fetch manPower list");
    }
  }
);

export const fetchManPowers = createAsyncThunk(
  "manPower/fetchManPower",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: ColumnFiltersState;
  }) => {
    try {
      const params: Record<string, any> = {
        page,
        page_size: pageSize,
      };

      if (search) params.search = search;
      if (sort) params.sort = sort;
      if (order) params.order = order;

      if (sort) {
        params.ordering = order === "desc" ? `-${sort}` : sort;
      }

      if (filters && Array.isArray(filters)) {
        filters.forEach((filter) => {
          if (filter.id && filter.value !== undefined && filter.value !== "") {
            params[filter.id] = filter.value;
          }
        });
      }

      const response: any = await Axios.get(`/manPowers/`, { params });

      const responseData = response.data;
      return {
        data: responseData.data || responseData.results || [],
        rowCount: responseData.count || 0,
      };
    } catch (error: any) {
      console.error(
        "fetchManPower error:",
        error.response?.data || error.message
      );
      throw new Error("Failed to fetch organisations");
    }
  }
);

export const fetchManPowersById = createAsyncThunk(
  "manPower/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/manPowers${id}/`);
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch fetchManPowersById by ID"
      );
    }
  }
);

export const updateManPower = createAsyncThunk(
  `/organisation/updateManPower`,
  async (packageData: any) => {
    const response: any = await Axios.put(
      `${API_URL}/manPower/${packageData.id}/`,
      packageData
    );

    return response;
  }
);
export const updateStatusInManPower = createAsyncThunk(
  `/manPower/updateStatusInManPower`,
  async (manPowersData: any) => {
    const response: any = await Axios.put(
      `/manPowers/${manPowersData?.manPower_id}/update/`,
      manPowersData
    );
    return response;
  }
);

export const addManPower = createAsyncThunk(
  `/manPower/addManPower`,
  async (packageData: any) => {
    const response = await Axios.post(`${API_URL}organisation/`, packageData);
    return response.data;
  }
);

export const deleteOrganisation = createAsyncThunk(
  `/manPower/delete`,
  async (id: number, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/${id}`, { headers });
      return id;
    } catch (err: any) {
      console.error("Delete error:", err);
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const exportManPowersCSV = createAsyncThunk(
  "manPowers/exportOManPowersCSV",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getManPower("authToken");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "*/*",
      };
      const response = await Axios.get("/manPowers/?export=csv/", {
        headers,
        responseType: "blob",
      });
      return {
        blob: response.data,
        filename:
          response.headers["content-disposition"]
            ?.split("filename=")[1]
            ?.replace(/"/g, "") || "manPowers.csv",
      };
    } catch (err: any) {
      console.error("Export CSV error:", err);
      return rejectWithValue(err.message || "Failed to export CSV");
    }
  }
);

export const fetchAllDropdowns = createAsyncThunk(
  "manPower/fetchAllDropdowns",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/manPower/dropdown/`);

      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch package list"
      );
    }
  }
);

export const fetchAllDropdownsManPowers = createAsyncThunk(
  "manPowers/fetchAllDropdownsManPowers",
  async () => {
    try {
      const [orgRes, manPowersRes, departmentRes] = await Promise.all([
        Axios.get(`${API_URL}/organization/all-org/`, { headers }),
        Axios.get(`${API_URL}/manPowers/get-all/`, { headers }),
        Axios.get(`${API_URL}/department/get-all/`, { headers }),
      ]);

      return {
        orgDropDown: Array.isArray(orgRes.data.data)
          ? orgRes.data.data.map((manPower: any) => manPower.organization_name)
          : [],
        departmentDropDown: Array.isArray(departmentRes.data.data)
          ? departmentRes.data.data.map(
              (manPower: any) => manPower.department_name
            )
          : [],
        manPowersDropDown: Array.isArray(manPowersRes.data.data)
          ? manPowersRes.data.data.map(
              (manPower: any) => manPower.manPower_name
            )
          : [],
      };
    } catch (err) {
      throw err;
    }
  }
);
